/**
 * Opens a blank tab synchronously (so it counts as a direct response to the user's click) and
 * returns a function that navigates it once the real URL is known. Safari (and other WebKit
 * browsers) only allow `window.open` from a direct, synchronous user gesture — calling it after
 * an `await` gets silently blocked as a popup, with no error to catch. Opening the tab now and
 * navigating it later keeps the gesture synchronous while still letting the URL depend on an
 * async response.
 */
export function openDeferredWindow(): (url: string) => void {
  const win = window.open('about:blank', '_blank');
  if (win) {
    // Best-effort `noopener`: can't pass the flag to `window.open` itself here, since that would
    // make it return null and leave us with nothing to navigate later. Clearing `opener` after
    // the fact closes the same reverse-tabnabbing gap for browsers that support it.
    try {
      win.opener = null;
    } catch {
      // Not supported everywhere — the synchronous open still succeeds either way.
    }
  }
  return (url: string) => {
    if (win) {
      win.location.href = url;
    } else {
      // The synchronous open was blocked too (e.g. popups disabled entirely for this site) —
      // fall back to the async open so the user isn't left with nothing to click.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
}
